'use client'

import { useState } from 'react'
import { useExtracted } from 'next-intl'
import { Play } from '@/components/ui/icons'

/**
 * In-site YouTube embed. Renders the thumbnail until clicked (keeps docs pages
 * light), then swaps in the standard YouTube player — native controls cover
 * speed, captions, mute, fullscreen, and "Watch on YouTube".
 */
export function YouTubePlayer({
  videoId,
  title,
  className = '',
  startSeconds,
  posterUrl,
}: {
  videoId: string
  title: string
  className?: string
  /** Optional start offset (seconds) for the embed, e.g. a chaptered walkthrough. */
  startSeconds?: number
  /** Optional thumbnail override; defaults to YouTube's auto-generated poster. */
  posterUrl?: string
}) {
  const t = useExtracted()
  const [activated, setActivated] = useState(false)

  if (!activated) {
    return (
      <button
        type="button"
        onClick={() => setActivated(true)}
        className={`group relative block w-full overflow-hidden rounded-xl transition-opacity hover:opacity-90 ${className}`}
        style={{ aspectRatio: '16 / 9' }}
        aria-label={t('Play {title}', { title })}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={posterUrl ?? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
          alt={title}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 transition-transform group-hover:scale-110">
            <Play className="h-5 w-5 translate-x-0.5 fill-white text-white" />
          </div>
        </div>
      </button>
    )
  }

  return (
    <div
      className={`relative w-full overflow-hidden rounded-xl bg-black ${className}`}
      style={{ aspectRatio: '16 / 9' }}
    >
      <iframe
        // `credentialless` lets the cross-origin YouTube embed load on canvas
        // routes, which set `COEP: credentialless` for the video engine
        // (see frontend/next.config.ts). Not yet in React's iframe types.
        {...({ credentialless: '' } as Record<string, string>)}
        // rel=0 keeps end-screen suggestions to our channel; iv_load_policy=3
        // hides annotations. YouTube no longer allows hiding the title bar or
        // logo (modestbranding/showinfo were retired) — this is the floor.
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&iv_load_policy=3&playsinline=1${startSeconds ? `&start=${startSeconds}` : ''}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  )
}
